import { useEffect, useState } from 'react';
import { Button, HStack, Input, Stack } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import useApiKeysStore from '@/utils/use-api-keys';
import { ApiKey } from '@/lib/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  apiKey: ApiKey | null;
}

export default function RenameApiKeyDialog({ isOpen, onClose, apiKey }: Props) {
  const { workspaceId } = useParams();
  const updateApiKey = useApiKeysStore((s) => s.updateApiKey);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (apiKey) {
      setName(apiKey.name);
      setError(null);
    }
  }, [apiKey]);

  function handleClose() {
    setSubmitting(false);
    setError(null);
    onClose();
  }

  async function handleSave() {
    if (!apiKey || !workspaceId) return;
    const trimmed = name.trim();
    if (trimmed.length === 0 || trimmed.length > 100) {
      setError('Name must be 1-100 characters.');
      return;
    }
    if (trimmed === apiKey.name) {
      handleClose();
      return;
    }
    setSubmitting(true);
    try {
      await updateApiKey(workspaceId, apiKey.id, { name: trimmed });
      toaster.create({ type: 'success', title: 'API key renamed' });
      handleClose();
    } catch (e: any) {
      toaster.create({
        type: 'error',
        title: 'Could not rename key',
        description: e?.response?.data?.error,
      });
      setSubmitting(false);
    }
  }

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={(d) => (!d.open ? handleClose() : null)}>
      <DialogBackdrop />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Rename API key</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={4}>
            <Field label='Name' required invalid={!!error} errorText={error ?? undefined}>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
                placeholder='e.g. Production sync'
                autoFocus
              />
            </Field>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <HStack>
            <Button variant='ghost' onClick={handleClose} type='button'>
              Cancel
            </Button>
            <Button colorPalette='brand' onClick={handleSave} loading={submitting}>
              Save
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
